import OptinsContext, { answerOptins, choiceType, OptinAnsweredInfo, OptinInfo, OptinUID } from '@context/OptinsContext';
import Button from '@ui/Button';
import React, { useContext, useMemo, useState } from 'react';
import { t } from 'i18n-js';
import './Optins.scss';
import useAutoLinkedText from '@helper/useAutoLinkedText';

let nextCheckForId = 0;

const OptinEntry: React.FC<{info: OptinAnsweredInfo, setAccepted: (accepted: boolean) => void, markError?: boolean}> = ({info, setAccepted, markError}) => {
  const className = [
    "teaserOptin",
    info.choiceType === 'checkbox' ? "teaserOptin--checkbox" : "teaserOptin--radio",
    markError ? "teaserOptin--errored" : "",
  ].join(" ")

  const autoLinkedLabel = useAutoLinkedText(t(`optins.${info.uid}.label`), true);

  const checkForId = useMemo(() => {
    nextCheckForId += 1;
    return `_checkId_${nextCheckForId}`;
  }, [])

  return <div className={className}>
    <div className="teaserOptin__inputHolder">
      {info.choiceType === 'radio'
      ? <>
          <label>
            <input type="radio" checked={info.accepted} onChange={() => setAccepted(true)} />
            {t(`optins.${info.uid}.radio_yes`)}
          </label>
          <label>
            <input type="radio" checked={info.madeAChoice && !info.accepted} onChange={() => setAccepted(false)} />
            {t(`optins.${info.uid}.radio_no`)}
          </label>
        </>
      : <div>
          <input id={checkForId} type="checkbox" checked={info.accepted} onChange={() => setAccepted(!info.accepted)} />
        </div>}
    </div>
    <div className="teaserOptin__textHolder">
      <label htmlFor={checkForId}>{autoLinkedLabel}</label>
      {info.required &&
        <div className="teaserOptin__requiredMarker">{t('optins.required')}</div>}
    </div>
  </div>
}

export interface OptinsRawProps {
  answeredInfos: OptinAnsweredInfo[];
  busy: boolean;
  submit: () => void;
  error?: string;
  setOptin: (uid: OptinUID, accepted: boolean) => void;
}

export const OptinsRaw: React.FC<OptinsRawProps> = ({busy, answeredInfos, setOptin, submit, error}) => {
  return <div className="teaserOptins__screen">
    <h1>{t('optins.confirm.title')}</h1>
    <div className="teaserOptins__list">
      {answeredInfos.map((i) => {
        return <OptinEntry
          key={i.uid}
          info={i}
          markError={!!error && ((i.required && !i.accepted) || (!i.madeAChoice))}
          setAccepted={(accepted) => setOptin(i.uid, accepted)} />        
      })}
    </div>
    <div className="teaserOptins__actionsHolder">
      {error &&
        <div className="teaserOptins__error">{error}</div>}
      <Button type="primaryOnDark" disabled={busy} onClick={submit}>{t('optins.confirm.continue_btn')}</Button>
    </div>
  </div>
}

const Optins: React.FC = () => {
  const { answeredInfos, markOptinsScreenToNotBeShown, answerOptin } = useContext(OptinsContext);
  
  const [state, setState] = useState<{busy: boolean, error?: string}>({
    busy: false,
  });

  const doSend = () => {
    setState((prev) => ({...prev, busy: true, error: undefined}))
    answerOptins(answeredInfos.filter((i) => i.madeAChoice).map((i) => ({uid: i.uid, accepted: i.accepted})))
      .then((resp) => {
        setState((prev) => ({
          ...prev,
          busy: false,
          error: resp.data.error || undefined,
        }))
        if (resp.data.success) {
          markOptinsScreenToNotBeShown();
        }
      })
      .catch((err) => {
        setState((prev) => ({...prev, busy: false, error: `${err}`}))
      })
  }

  return <OptinsRaw
    busy={state.busy}
    answeredInfos={answeredInfos}
    setOptin={answerOptin}
    submit={doSend}
    error={state.error}
  />
}

export default Optins;
